import { VentureInvitation } from './venture-invitation';
import { WebsiteCard } from './website-card';
import { saveArtifact } from './artifact-download';
import React, { useState, useRef, useEffect } from 'react';
import { View, ScrollView, Image, Platform } from 'react-native';
import { CURRENT_USER, dmChannel } from '@ventureroom/core';
import { useApp, useData, useTheme, useMe } from './provider';
import {
  Avatar,
  Button,
  Card,
  Column,
  Copy,
  Empty,
  Input,
  Page,
  Row,
  Tag,
  dateLabel,
  timeLabel,
} from './components';
export function Conversation({
  channel,
  boardroom = false,
}: {
  channel: string;
  boardroom?: boolean;
}) {
  const data = useData();
  const app = useApp();
  const { act, busy } = app;
  const t = useTheme();
  const me = useMe();
  const canUpload =
    (me.role === 'Business Owner' || me.role === 'Both') && me.subscriptionTier === 'enterprise';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const scrollViewRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fileInputRef = useRef<any>(null);
  const [attachment, setAttachment] = useState<{
    name: string;
    url: string;
    isImage: boolean;
  } | null>(null);
  const [draft, setDraft] = useState(''),
    [reply, setReply] = useState<string | undefined>();
  const messages = data.messages
    .filter((m) => m.channel === channel)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  useEffect(() => {
    scrollViewRef.current?.scrollToEnd({ animated: false });
  }, [channel, messages.length]);
  const replying = messages.find((m) => m.id === reply);
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 24000) {
      app.fail('Choose an attachment smaller than 24 KB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () =>
      setAttachment({
        name: file.name,
        url: String(reader.result),
        isImage: file.type.startsWith('image/'),
      });
    reader.readAsDataURL(file);
    event.target.value = '';
  };
  async function send() {
    const text = draft;
    if (!text.trim() && !attachment) return;
    setDraft('');
    setReply(undefined);
    const attachUrl = attachment?.url;
    const attachName = attachment?.name;
    const isImage = attachment?.isImage;
    setAttachment(null);
    const success = await act(
      {
        type: 'message',
        channel,
        text: text || (attachName ? `Sent an attachment: ${attachName}` : ''),
        replyTo: reply,
        ...(isImage ? { imageUrl: attachUrl } : {}),
        ...(!isImage && attachUrl ? { fileUrl: attachUrl, fileName: attachName } : {}),
      },
      'Message sent',
    );
    if (!success) {
      setDraft(text);
      if (attachUrl && attachName)
        setAttachment({ name: attachName, url: attachUrl, isImage: isImage ?? false });
    }
  }
  return (
    <Column gap={20} style={{ flex: 1 }}>
      {app.mode === 'local' &&
        (channel.includes('ai-assistant') || channel.startsWith('venturebot-')) && (
          <Copy muted>
            Connect the local backend in Settings to work with this AI collaborator.
          </Copy>
        )}
        {app.tasks
          .filter((task) => task.channel === channel)
          .slice(0, 1)
          .filter((task) => task.status !== 'completed' || task.collaboratorId === 'dispatch')
          .map((task) => (
            <Card key={task.id} style={{ paddingVertical: 12 }}>
              <Row between wrap>
                <Copy size={13} bold>
                  {task.status === 'queued'
                    ? 'Queued'
                    : task.status === 'analysing'
                      ? 'Reviewing'
                      : task.status === 'working'
                        ? 'Working'
                        : task.status === 'awaiting_clarification'
                          ? 'Needs your reply'
                          : task.status === 'failed'
                            ? 'Could not finish'
                            : task.status === 'cancelled'
                              ? 'Cancelled'
                              : 'Completed'}
                </Copy>
                {['queued', 'analysing', 'working'].includes(task.status) && (
                  <Button kind="quiet" onPress={() => void app.cancelTask(task.id)}>
                    Cancel task
                  </Button>
                )}
              </Row>
              <Copy size={12} muted>
                {task.description}
                {task.collaboratorId === 'dispatch' && task.result ? ` · ${task.result}` : ''}
              </Copy>
              {task.error && <Copy size={12} color={t.danger}>Task status: {task.error}</Copy>}
            </Card>
          ))}
      <ScrollView
        ref={scrollViewRef}
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingBottom: 20, gap: 20 }}
      >
        {!messages.length && <Empty title="Say hello" detail="Start your conversation." />}
        {messages.map((m) => {
          const person = data.profiles.find((p) => p.id === m.author)!;
          const own = m.author === CURRENT_USER;
          const parent = messages.find((p) => p.id === m.replyTo);
          return (
            <Row
              key={m.id}
              style={{ alignItems: 'flex-start', flexDirection: own ? 'row-reverse' : 'row' }}
            >
              <Avatar person={person} size={36} />
              <View style={{ maxWidth: '82%', minWidth: 160, gap: 6 }}>
                <Row between wrap>
                  <Copy size={12} bold>
                    {person.name.split(' ')[0]}
                  </Copy>
                  <Copy muted size={10}>
                    {dateLabel(m.createdAt)} · {timeLabel(m.createdAt)}
                  </Copy>
                </Row>
                <View
                  style={{
                    padding: 16,
                    borderRadius: 18,
                    borderTopRightRadius: own ? 4 : 18,
                    borderTopLeftRadius: own ? 18 : 4,
                    backgroundColor: own ? t.hero : t.surface,
                    borderWidth: 0,
                    borderColor: own ? t.hero : t.border,
                    gap: 8,
                  }}
                >
                  {parent && (
                    <View style={{ borderLeftWidth: 2, borderColor: t.accent, paddingLeft: 9 }}>
                      <Copy muted size={12}>
                        {parent.text}
                      </Copy>
                    </View>
                  )}
                  <Copy>{m.text}</Copy>
                  {!!m.invitationId && <VentureInvitation id={m.invitationId} />}
                  {!!m.fileUrl && Platform.OS === 'web' && (
                    <a href={m.fileUrl} download={m.fileName ?? 'attachment'}>
                      {m.fileName ?? 'Download attachment'}
                    </a>
                  )}
                  {!!m.websiteCode && (
                    <WebsiteCard site={m.websiteCode} artifactId={m.artifactId} />
                  )}
                  {!!m.artifactId && !m.websiteCode && (
                    <Button
                      onPress={() =>
                        void app
                          .artifact(m.artifactId!)
                          .then((bytes) => saveArtifact(bytes, 'deliverable.txt', 'text/plain'))
                          .catch(() => app.fail('Download failed. Please retry.'))
                      }
                    >
                      Download deliverable
                    </Button>
                  )}
                  {!!m.imageUrl && (
                    <Image
                      source={{ uri: m.imageUrl }}
                      style={{
                        width: '100%',
                        maxWidth: 256,
                        aspectRatio: 1,
                        borderRadius: 12,
                        marginTop: 8,
                      }}
                    />
                  )}
                  {!!m.reference && <Tag>↗ {m.reference}</Tag>}
                </View>
                <Row>
                  <Button
                    compact
                    kind="quiet"
                    label={`Reply to ${person.name}`}
                    onPress={() => setReply(m.id)}
                  >
                    Reply
                  </Button>
                  <Button
                    compact
                    kind="quiet"
                    label={`Appreciate message from ${person.name}`}
                    onPress={() => void act({ type: 'react', id: m.id }, 'Reaction updated')}
                  >
                    {m.reactions.includes(CURRENT_USER) ? '♥' : '♡'} {m.reactions.length || ''}
                  </Button>
                </Row>
              </View>
            </Row>
          );
        })}
        {boardroom &&
          data.activities
            .filter((a) => a.ventureId === channel && a.kind === 'commitment')
            .slice(0, 2)
            .map((a) => (
              <Copy key={a.id} muted size={12}>
                ✓ {a.title}
              </Copy>
            ))}
        {busy && Platform.OS === 'web' && channel.includes('ai-assistant') && (
          <Row style={{ alignItems: 'flex-start', flexDirection: 'row' }}>
            <View
              style={{
                width: 36,
                height: 36,
                borderRadius: 18,
                backgroundColor: '#ddd',
                overflow: 'hidden',
              }}
            >
              <Image
                source={{
                  uri: 'https://ui-avatars.com/api/?name=Venture+Bot&background=e0e0e0&color=333',
                }}
                style={{ width: 36, height: 36 }}
              />
            </View>
            <View
              style={{
                backgroundColor: t.border,
                padding: 16,
                borderRadius: 12,
                borderTopLeftRadius: 4,
                marginLeft: 12,
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <style>{`
                  @keyframes bounce {
                    0%, 100% { transform: translateY(0); opacity: 0.4; }
                    50% { transform: translateY(-4px); opacity: 1; }
                  }
                  .dot { width: 6px; height: 6px; border-radius: 3px; background-color: #888; margin: 0 2px; animation: bounce 1.4s infinite ease-in-out both; }
                  .dot1 { animation-delay: -0.32s; }
                  .dot2 { animation-delay: -0.16s; }
                  .dot3 { animation-delay: 0s; }
                `}</style>
              <div className="dot dot1" />
              <div className="dot dot2" />
              <div className="dot dot3" />
            </View>
          </Row>
        )}
      </ScrollView>
      <Card>
        {replying && (
          <Row between>
            <View style={{ flex: 1 }}>
              <Copy muted size={12}>
                Replying to: {replying.text}
              </Copy>
            </View>
            <Button compact kind="quiet" onPress={() => setReply(undefined)}>
              Cancel
            </Button>
          </Row>
        )}
        {attachment && (
          <Row
            between
            style={{
              backgroundColor: t.surfaceAlt,
              padding: 12,
              borderRadius: 8,
              alignItems: 'center',
            }}
          >
            <Copy size={13} bold>
              {attachment.name}
            </Copy>
            <Button compact kind="quiet" onPress={() => setAttachment(null)}>
              Remove
            </Button>
          </Row>
        )}
        <Input
          label={boardroom ? 'Message the boardroom' : 'Your message'}
          value={draft}
          onChange={setDraft}
          onSubmit={() => {
            if (draft.trim() || attachment) void send();
          }}
          multiline
          placeholder="Message…"
        />
        <Row between>
          <Row style={{ gap: 8 }}>
            {(boardroom || channel.startsWith('venturebot-')) &&
              canUpload &&
              Platform.OS === 'web' && (
                <>
                  <input
                    type="file"
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    ref={fileInputRef as any}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                  <Button kind="secondary" compact onPress={() => fileInputRef.current?.click()}>
                    + Attach file
                  </Button>
                </>
              )}
          </Row>
          <Button disabled={!draft.trim() && !attachment} onPress={() => void send()}>
            Send ↗
          </Button>
        </Row>
      </Card>
    </Column>
  );
}
export function ChatScreen({ personId }: { personId: string }) {
  const data = useData();
  const { navigate, act } = useApp();
  const p = data.profiles.find((p) => p.id === personId);
  if (!p)
    return (
      <Page title="Chat unavailable" back="connections">
        <Empty title="Person not found" detail="Open a conversation from Chats." />
      </Page>
    );
  const matched = data.connections.some(
    (c) =>
      c.status === 'accepted' &&
      [c.from, c.to].includes(CURRENT_USER) &&
      [c.from, c.to].includes(personId),
  );
  const invitations = data.invitations.filter(
    (i) =>
      i.kind === 'invitation' &&
      [i.from, i.to].includes(CURRENT_USER) &&
      [i.from, i.to].includes(personId),
  );
  const shared = data.ventures.filter(
    (v) => v.members.includes(CURRENT_USER) && v.members.includes(personId),
  );
  return (
    <Page
      title={p.name}
      back="connections"
      scroll={false}
      action={
        <Button
          compact
          kind="quiet"
          label={`View ${p.name}'s profile`}
          onPress={() => navigate(`person/${p.id}`)}
        >
          Profile
        </Button>
      }
    >
      {matched ? (
        <>
          {p.is_ai && (
            <Row between>
              <Copy muted size={12}>
                AI Collaborator · {p.ai?.profession ?? p.background}
              </Copy>
              <Button
                kind="quiet"
                onPress={() => {
                  const connection = data.connections.find(
                    (c) =>
                      c.status === 'accepted' &&
                      [c.from, c.to].includes(CURRENT_USER) &&
                      [c.from, c.to].includes(p.id),
                  );
                  if (connection)
                    void act({ type: 'connectionArchive', id: connection.id }).then((ok) => {
                      if (ok) navigate('connections');
                    });
                }}
              >
                End collaboration
              </Button>
            </Row>
          )}
          {!!shared.length && (
            <Row wrap>
              {shared.map((v) => (
                <Button
                  key={v.id}
                  compact
                  kind="secondary"
                  onPress={() => navigate(`venture/${v.id}`)}
                >
                  {v.name}
                </Button>
              ))}
            </Row>
          )}
          <Conversation channel={dmChannel(CURRENT_USER, personId)} />
        </>
      ) : invitations.length ? (
        <ScrollView contentContainerStyle={{ gap: 20, paddingBottom: 20 }}>
          {invitations.map((invitation) => {
            const message = data.messages.find((m) => m.invitationId === invitation.id);
            const author = data.profiles.find((p) => p.id === invitation.from);
            return (
              <Card key={invitation.id}>
                <Copy bold size={12}>
                  {author?.name ?? 'Inviter'}
                </Copy>
                <Copy>
                  {message?.text ??
                    `I’d like to invite you to ${data.ventures.find((v) => v.id === invitation.ventureId)?.name ?? 'my venture'}.`}
                </Copy>
                <VentureInvitation id={invitation.id} />
              </Card>
            );
          })}
          <Copy muted size={12}>
            Accepting an invitation joins the venture and connects you. Ordinary chat starts after
            acceptance.
          </Copy>
        </ScrollView>
      ) : (
        <Empty
          title="Match to chat"
          detail="You can start a conversation once you have both liked each other."
          action={<Button onPress={() => navigate('discover')}>Discover people</Button>}
        />
      )}
    </Page>
  );
}

export function MeetingRoomScreen({ ownerId }: { ownerId: string }) {
  const data = useData();
  const owner = data.profiles.find((p) => p.id === ownerId);

  const hasAccess = owner?.id === CURRENT_USER || owner?.team?.includes(CURRENT_USER);
  if (!owner || !hasAccess) {
    return (
      <Page title="Meeting room" back="connections">
        <Empty title="Room not available" detail="You are not part of this team." />
      </Page>
    );
  }

  return (
    <Page title={`${owner.name}'s Meeting Room`} back="connections" scroll={false}>
      <Conversation channel={`team:${ownerId}`} />
    </Page>
  );
}
